import {
    Fab,
    Table,
    Select,
    TableRow,
    MenuItem,
    TableBody,
    TableCell,
    TableHead,
    TextField,
    Typography,
    CircularProgress,
    Checkbox
} from '@mui/material';
import { useSelector } from 'react-redux';
import { getBaseURL } from '../../baseURL';
import ErrorIcon from '@mui/icons-material/Error';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import { green, red } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import { Persona } from '@fluentui/react/lib/Persona';
import { Container, Paper } from '@mui/material';

export const Step3AccessRequests = () => {
    const useStyles = makeStyles(theme => ({
        root: {
            padding: theme.spacing(3, 2),
        },
        fabProgress: {
            color: green[500],
            position: 'absolute',
            top: -18,
            left: -3,
            zIndex: 1,
        },
        wrapper: {
            margin: theme.spacing(1),
            position: 'relative',
        },
        buttonGreen: {
            backgroundColor: green[500],
            '&:hover': {
                backgroundColor: green[700],
            },
        },
        buttonRed: {
            backgroundColor: red[500],
            '&:hover': {
                backgroundColor: red[700],
            },
        },
    }));

    const classes = useStyles();
    const currentUser = useSelector(state => state.auth);
    const config = {
        headers: { Authorization: 'Bearer ' + currentUser.jwtIdToken.idToken },
    };

    const [isAdmin, setIsAdmin] = useState(null);
    const [accessRequests, setAccessRequests] = useState([]);
    const [checkedRequests, setCheckedRequests] = useState(false);
    const [classifications, setClassifications] = useState([]);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = () => {
        if (accessRequests.length < 1 && !checkedRequests) {
            Axios.get(`${getBaseURL()}/admin/accessrequests`, config)
                .then(response => {
                    const requests = [...response.data];
                    requests.forEach(x => (x.submitted = { Submitted: false, Error: false, Message: '' }));
                    setAccessRequests(requests);
                    setCheckedRequests(true);
                })
                .catch(() => {});
        }

        if (classifications.length < 1) {
            Axios.get(`${getBaseURL()}/admin/getclassificationlevelsforadmin`, config)
                .then(response => {
                    setClassifications(response.data);
                })
                .catch(() => {});
        }

        if (isAdmin === null) {
            try {
                if (currentUser.user.permissions.find(p => p.permissionName === 'Step3 Admin').value === true) {
                    setIsAdmin(true);
                } else {
                    setIsAdmin(false);
                }
            } catch (error) {
                setIsAdmin(false);
            }
        }
    };

    const certifiedCountries = new Set([
        'australia', 'canada', 'france', 'germany', 'ireland', 'japan',
        'netherlands', 'new zealand', 'poland', 'singapore', 'sweden',
        'united kingdom', 'united states'
    ]);

    const getClassLevel = request => request?.classificationLevelID ?? request?.classificationLevel?.id ?? 0;

    const getDerivedMaxStep = request => {
        const classLevel = Number(getClassLevel(request) || 0);
        const printRequested = request?.requestPrint === true;
        const emailRequested = request?.requestEmail === true;
        const operatingGroup = (request?.operatingGroup ?? '').trim().toLowerCase();
        const isDistributor = operatingGroup !== 'oe';
        const isOE = !isDistributor;
        const isCertifiedCountry = certifiedCountries.has((request?.country ?? '').trim().toLowerCase());
        const isAdditionalPermissions = classLevel === 3 || classLevel === 4;
        const isUnrestrictedAccess = classLevel === 5;

        const isOnlyStep1 =
            classLevel === 2 ||
            ((classLevel === 3 || classLevel === 4) && isOE && !printRequested && !emailRequested);

        if (isOnlyStep1) return 1;
        if (isCertifiedCountry) return 2;

        if (isDistributor && isAdditionalPermissions) {
            if (isUnrestrictedAccess || printRequested || emailRequested) return 5;
            return 4;
        }

        if (isDistributor && (isUnrestrictedAccess || printRequested || emailRequested)) return 5;

        return 1;
    };

    const filteredRequests = React.useMemo(() => {
        const list = accessRequests ?? [];
        return list.filter(
            r =>
                getDerivedMaxStep(r) >= 3 &&
                r?.step3Approved !== true &&
                r?.step1Approved === true &&
                r?.state === null &&
                r?.step2Approved === true
        );
    }, [accessRequests]);

    const commentsToUser = (requestID, event) => {
        const requests = [...accessRequests];
        requests.find(x => x.id === requestID).commentsToUser = event.target.value;
        setAccessRequests(requests);
    };

    const onAccessLevelChange = (userID, event) => {
        const requests = [...accessRequests];
        const selectedClassification = classifications.find(x => x.id === event.target.value);
        const request = requests.find(x => x.userID === userID);
        if (!request) return;

        request.classificationLevel = selectedClassification;
        request.classificationLevelID = selectedClassification?.id;
        setAccessRequests(requests);
    };

    const onPrintChange = event => {
        const requests = [...accessRequests];
        requests.find(x => x.userID === event.target.id).requestPrint = event.target.checked;
        setAccessRequests(requests);
    };

    const onEmailChange = event => {
        const requests = [...accessRequests];
        requests.find(x => x.userID === event.target.id).requestEmail = event.target.checked;
        setAccessRequests(requests);
    };

    const approveDeny = (userID, approve) => {
        const userRequest = accessRequests.find(x => x.userID === userID);
        if (!userRequest) return;

        const request = {
            guid: userRequest.user.guid,
            classLevel: userRequest.classificationLevelID ?? userRequest.classificationLevel?.id,
            requestPrint: userRequest.requestPrint,
            requestEmail: userRequest.requestEmail,
            country: userRequest.country,
            operatingGroup: userRequest.operatingGroup,
            jobType: userRequest.jobType,
            functionalRole: userRequest.functionalRole,
            isCertifiedCountry: userRequest.isCertifiedCountry,
            state: approve,
            commentsToUser: userRequest.commentsToUser,
            currentStep: 3,
            id: userRequest.id,
        };

        let requests = [...accessRequests];
        if (approve) {
            requests.find(x => x.userID === userID).SendingApprove = true;
        } else {
            requests.find(x => x.userID === userID).SendingReject = true;
        }
        setAccessRequests(requests);

        Axios.post(`${getBaseURL()}/admin/accessrequests/update`, request, config)
            .then(() => {
                requests = [...accessRequests];
                requests.find(x => x.userID === userID).submitted = {
                    Submitted: true,
                    Error: false,
                    Message: approve ? 'APPROVED' : 'REJECTED',
                };
                requests.find(x => x.userID === userID).SendingApprove = false;
                requests.find(x => x.userID === userID).SendingReject = false;
                setAccessRequests(requests);
                loadData();
            })
            .catch(() => {});
    };

    const getUserPersonaText = user => {
        if (user.firstName && user.lastName) {
            return user.firstName + ' ' + user.lastName;
        }
        return user.email;
    };

    return (
        <Container maxWidth={false} disableGutters={true}>
            {isAdmin ? (
                <Container maxWidth={false} disableGutters={true}>
                    <Typography variant="h4" gutterBottom>
                        Step3 Access Requests
                    </Typography>
                    {checkedRequests ? (
                        filteredRequests.length > 0 ? (
                            <Paper className={classes.root}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>User</TableCell>
                                            <TableCell>Existing Access Level</TableCell>
                                            <TableCell>Requested Access Level</TableCell>
                                            <TableCell>Print</TableCell>
                                            <TableCell>Email Access</TableCell>
                                            <TableCell>Physical Location</TableCell>
                                            <TableCell>Operating Group</TableCell>
                                            <TableCell>Functional Role</TableCell>
                                            <TableCell>Job Type</TableCell>
                                            <TableCell>Other Access Requested</TableCell>
                                            <TableCell>Comments to User</TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredRequests.map((item, index) => {
                                            return (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <Persona
                                                            text={getUserPersonaText(item.user)}
                                                            secondaryText={item.user.jobTitle}
                                                            imageUrl={'data:image/png;base64,' + item.user.photo}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {item.existingClassLevelDesc || 'NA'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Select
                                                            id={item.userID}
                                                            value={item.classificationLevelID ?? item.classificationLevel?.id ?? ''}
                                                            variant="standard"
                                                            onChange={e => onAccessLevelChange(item.userID.toString(), e)}
                                                        >
                                                            {classifications.map((classification, idx) => {
                                                                return (
                                                                    <MenuItem key={idx} value={classification.id}>
                                                                        {classification.description}
                                                                    </MenuItem>
                                                                );
                                                            })}
                                                        </Select>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Checkbox
                                                            id={item.userID}
                                                            color="primary"
                                                            checked={item.requestPrint === true}
                                                            onChange={onPrintChange}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Checkbox
                                                            id={item.userID}
                                                            color="primary"
                                                            checked={item.requestEmail === true}
                                                            onChange={onEmailChange}
                                                        />
                                                    </TableCell>
                                                    <TableCell>{item.country}</TableCell>
                                                    <TableCell>{item.operatingGroup}</TableCell>
                                                    <TableCell>{item.functionalRole}</TableCell>
                                                    <TableCell>{item.jobType}</TableCell>
                                                    <TableCell>{item.accessReason || 'NA'}</TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            id="commentsToUser"
                                                            variant="standard"
                                                            multiline
                                                            value={item.commentsToUser || ''}
                                                            onChange={e => commentsToUser(item.id, e)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {!item.submitted.Submitted ? (
                                                            <span>
                                                                <span className={classes.wrapper}>
                                                                    <Fab
                                                                        id={item.userID}
                                                                        size="medium"
                                                                        color="primary"
                                                                        onClick={() => approveDeny(item.userID, false)}
                                                                        disabled={item.SendingReject || item.SendingApprove}
                                                                    >
                                                                        <ClearIcon />
                                                                    </Fab>
                                                                    {item.SendingReject && (
                                                                        <CircularProgress size={52} className={classes.fabProgress} />
                                                                    )}
                                                                </span>
                                                                <span className={classes.wrapper}>
                                                                    <Fab
                                                                        size="medium"
                                                                        color="primary"
                                                                        onClick={() => approveDeny(item.userID, true)}
                                                                        disabled={item.SendingReject || item.SendingApprove}
                                                                    >
                                                                        <CheckIcon />
                                                                    </Fab>
                                                                    {item.SendingApprove && (
                                                                        <CircularProgress size={52} className={classes.fabProgress} />
                                                                    )}
                                                                </span>
                                                            </span>
                                                        ) : (
                                                            <Typography variant="subtitle1">{item.submitted.Message}</Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Paper>
                        ) : (
                            <Paper className={classes.root}>
                                <Typography variant="h6">No Requests Found</Typography>
                            </Paper>
                        )
                    ) : (
                        <CircularProgress color="primary" />
                    )}
                </Container>
            ) : (
                <Paper className={classes.root}>
                    <Typography variant="h6">
                        <ErrorIcon /> Access Denied
                    </Typography>
                </Paper>
            )}
        </Container>
    );
};
